import { Component, OnInit, inject } from '@angular/core';
import { FormGroup, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';
import { MatStepperModule } from '@angular/material/stepper';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatTimepickerModule } from '@angular/material/timepicker';
import { RouterModule } from '@angular/router';
import { provideNativeDateAdapter } from '@angular/material/core';
import { AmplifyModelService } from '../../services/amplify-model-service.service';
import {
  ServiceAddressPopupComponent,
  ServiceAddressPopupData
} from '../../components/service-address-popup/service-address-popup.component';

export interface AppointmentBuilderData {
  scheduleId?: string;
  customerId?: string;
  subId?: string;
  initialDate?: string;
  initialTime?: string;
}

@Component({
    selector: 'app-appointment-builder',
    providers: [provideNativeDateAdapter()],
    imports: [
      MatStepperModule,
      MatButtonModule,
      MatDialogModule,
      MatSnackBarModule,
      MatFormFieldModule,
      MatInputModule,
      MatSelectModule,
      MatDatepickerModule,
      MatTimepickerModule,
      RouterModule,
      ReactiveFormsModule,
      FormsModule
    ],
    templateUrl: './appointment-builder.component.html',
    styleUrl: './appointment-builder.component.css'
})
export class AppointmentBuilderComponent implements OnInit {
  appointmentObj!: any;
  appointmentMessage: string = '';
  
  // Time restriction configuration loaded from FieldConfig
  timeRestrictions: any = {
    minTime: null,
    maxTime: null,
    timeStep: 30 // default step
  };
  
  // Cached values to prevent excessive function calls
  cachedMinTime: string | null = null;
  cachedMaxTime: string | null = null;
  
  // Form data models
  appointmentData: any = {
    appointment_type: '',
    scheduleId: '',
    name: '',
    start_date: null,
    start_time: null,
    end_date: null,
    end_time: null,
    subId: '',
    serviceTypeId: '', // NEW - required Service Type field, drives End_Date_Time auto-calculation + appointment name
    serviceIds: [] // NEW - multi-select Services field (SERVICE appointments only), scoped to the selected ServiceAddress
  };

  // Data arrays for dropdowns
  allSchedules: any[] = [];
  filteredSchedules: any[] = [];
  subscriptions: any[] = [];
  filteredSubscriptions: any[] = [];
  leads: any[] = [];
  customers: any[] = [];
  serviceTypes: any[] = []; // NEW - all ServiceType records, for the required Service Type dropdown
  // NEW - Services already linked to the selected ServiceAddress via
  // ServiceServiceAddress, scoping the multi-select Services field's
  // options. Reloaded whenever selectedServiceAddress changes (see
  // handleServiceAddressResult()).
  servicesForSelectedAddress: any[] = [];

  // True once the appointment name has been manually edited by the user -
  // once set, updateAppointmentName() (task 5) stops overwriting
  // appointmentData.name automatically, so a technician's own edit is never
  // silently clobbered.
  nameManuallyEdited = false;
  
  // Location selection for step 2
  selectedLocationRecord: any = null;

  // ServiceAddress-first location selection (Req 1.1/1.2) - SERVICE (and, in a
  // later task, SALES) appointments resolve their location through this
  // rather than through selectedLocationRecord's Sub-selection flow.
  selectedServiceAddress: any = null;
  linkedSubsForServiceAddress: any[] = []; // populated when the ServiceAddress is linked to >1 Sub (Req 1.7)

  // Customer filtering (UI only, not saved to appointment)
  selectedCustomerFilter: any = null;
  
  // Static appointment type options (Req 15.1 - appointment_type is a fixed enum)
  appointmentTypeOptions = [
    { value: 'SERVICE', title: 'Service Appointment' },
    { value: 'SALES', title: 'Sales Appointment' },
    { value: 'CONSULTATION', title: 'Consultation' },
  ];

  readonly dialogRef = inject(MatDialogRef<AppointmentBuilderComponent>);
  readonly data = inject<AppointmentBuilderData>(MAT_DIALOG_DATA);
  private ams = inject(AmplifyModelService);
  private dialog = inject(MatDialog);

  async ngOnInit() {
    console.log('🏁 AppointmentBuilder: Starting initialization');
    
    // STEP 1: Load all FieldConfigs first (before any UI setup)
    await this.loadTimeRestrictions();
    
    // STEP 2: Now load data for dropdowns (can use FieldConfig data if needed)
    await this.loadSchedules();
    await this.loadCustomers();
    await this.loadSubscriptions();
    await this.loadLeads();
    await this.loadServiceTypes();
    
    console.log('✅ AppointmentBuilder: Initialization complete. Time restrictions:', this.timeRestrictions);
    
    // STEP 3: Set up initial values if provided (now that configs are loaded)
    if (this.data) {
      if (this.data.scheduleId) {
        this.appointmentData.scheduleId = this.data.scheduleId;
      }
      if (this.data.subId) {
        this.appointmentData.subId = this.data.subId;
      }
      if (this.data.initialDate) {
        this.appointmentData.start_date = new Date(this.data.initialDate);
        this.appointmentData.end_date = new Date(this.data.initialDate);
      }
      if (this.data.initialTime) {
        // Convert time string to Date object for timepicker
        const timeDate = new Date();
        const [hours, minutes] = this.data.initialTime.split(':');
        timeDate.setHours(parseInt(hours), parseInt(minutes));
        
        // Apply time restrictions if configured (restrictions are now loaded)
        const restrictedTime = this.applyTimeRestrictions(timeDate);
        this.appointmentData.start_time = restrictedTime;
        
        // Default end time to 1 hour later
        const endTime = new Date(restrictedTime);
        endTime.setHours(endTime.getHours() + 1);
        this.appointmentData.end_time = this.applyTimeRestrictions(endTime);
      }
    }
    
    // No need for manual change detection since configs are loaded before template binding
  }

  /**
   * Load time restrictions from FieldConfig for start_date_time and end_date_time fields
   */
  async loadTimeRestrictions() {
    try {
      console.log('🕐 Loading time restrictions from FieldConfig');
      
      // Load FieldConfig for start_date_time (use this as the template for restrictions)
      const startTimeConfig = await this.ams.getChoiceConfig('ScheduleEntry', 'start_date_time');
      
      console.log('🕐 Raw FieldConfig result:', startTimeConfig);
      
      if (startTimeConfig && startTimeConfig.component_properties_json) {
        console.log('🕐 Raw JSON string:', startTimeConfig.component_properties_json);
        
        const componentProps = JSON.parse(startTimeConfig.component_properties_json);
        
        this.timeRestrictions = {
          minTime: componentProps.minTime || null,
          maxTime: componentProps.maxTime || null,
          timeStep: componentProps.timeStep || 30,
          businessHoursOnly: componentProps.businessHoursOnly || false
        };
        
        // Cache the values for the timepicker
        this.cachedMinTime = this.timeRestrictions.minTime;
        this.cachedMaxTime = this.timeRestrictions.maxTime;
        
        console.log('✅ Parsed and loaded time restrictions:', this.timeRestrictions);
        console.log('🕐 Cached values set - minTime:', this.cachedMinTime, 'maxTime:', this.cachedMaxTime);
      } else {
        console.log('ℹ️ No time restrictions configured, using defaults');
        console.log('🕐 FieldConfig result details:', {
          hasConfig: !!startTimeConfig,
          hasJson: startTimeConfig?.component_properties_json ? 'yes' : 'no',
          jsonValue: startTimeConfig?.component_properties_json
        });
        
        // Ensure cached values are null if no config
        this.cachedMinTime = null;
        this.cachedMaxTime = null;
      }
    } catch (error) {
      console.error('❌ Error loading time restrictions:', error);
      // Ensure cached values are null on error
      this.cachedMinTime = null;
      this.cachedMaxTime = null;
    }
  }

  /**
   * Apply time restrictions to a given time
   */
  applyTimeRestrictions(time: Date): Date {
    if (!this.timeRestrictions.minTime || !this.timeRestrictions.maxTime) {
      return time; // No restrictions configured
    }

    const timeString = time.toTimeString().substring(0, 5); // HH:MM format
    const timeMinutes = this.convertTimeToMinutes(timeString);
    const minMinutes = this.convertTimeToMinutes(this.timeRestrictions.minTime);
    const maxMinutes = this.convertTimeToMinutes(this.timeRestrictions.maxTime);

    // If time is before minimum, set to minimum
    if (timeMinutes < minMinutes) {
      const [minHours, minMins] = this.timeRestrictions.minTime.split(':').map(Number);
      const adjustedTime = new Date(time);
      adjustedTime.setHours(minHours, minMins, 0, 0);
      return adjustedTime;
    }

    // If time is after maximum, set to maximum
    if (timeMinutes > maxMinutes) {
      const [maxHours, maxMins] = this.timeRestrictions.maxTime.split(':').map(Number);
      const adjustedTime = new Date(time);
      adjustedTime.setHours(maxHours, maxMins, 0, 0);
      return adjustedTime;
    }

    // Time is within bounds
    return time;
  }

  /**
   * Get minimum time as string for Material timepicker
   */
  getMinTime(): string | null {
    return this.cachedMinTime;
  }

  /**
   * Get maximum time as string for Material timepicker
   */
  getMaxTime(): string | null {
    return this.cachedMaxTime;
  }

  /**
   * Get time step for Material timepicker interval (supports both seconds and "Nm" format)
   */
  getTimeStepMinutes(): number | string {
    return this.timeRestrictions.timeStep || 30;
  }

  /**
   * Helper method to convert time string (HH:MM) to minutes since midnight
   */
  private convertTimeToMinutes(time: string): number {
    if (!time) return 0;
    const [hours, minutes] = time.split(':').map(Number);
    return (hours || 0) * 60 + (minutes || 0);
  }

  async loadSchedules() {
    try {
      // Load all schedules with their associated users
      const response = await this.ams.getItemsForModel('Schedule');
      
      if (response && response.data) {
        this.allSchedules = response.data;
        
        // Load user data for each schedule
        for (let schedule of this.allSchedules) {
          if (schedule.userId) {
            try {
              const userResponse = await this.ams.getItemForModel('User', schedule.userId);
              if (userResponse && userResponse.data) {
                schedule.user = userResponse.data;
              }
            } catch (error) {
              console.error(`Error loading user for schedule ${schedule.id}:`, error);
            }
          }
        }
        
        console.log('Loaded schedules:', this.allSchedules);
      }
    } catch (error) {
      console.error('Error loading schedules:', error);
    }
  }

  async loadCustomers() {
    try {
      // Load customers for filtering subscriptions
      const response = await this.ams.getItemsForModel('Customer');
      
      if (response && response.data) {
        this.customers = response.data.sort((a, b) => a.name.localeCompare(b.name));
        console.log('Loaded customers:', this.customers);
      }
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  }

  async loadSubscriptions() {
    try {
      // Load subscriptions for the dropdown
      const response = await this.ams.getItemsForModel('Sub');
      
      if (response && response.data) {
        this.subscriptions = response.data;
        
        // Load customer data and contact info for each subscription
        for (let sub of this.subscriptions) {
          if (sub.customerId) {
            try {
              const customerResponse = await this.ams.getItemForModel('Customer', sub.customerId);
              if (customerResponse && customerResponse.data) {
                sub.customer = customerResponse.data;
              }
            } catch (error) {
              console.error(`Error loading customer for subscription ${sub.id}:`, error);
            }
          }
        }
        
        // Initialize filtered subscriptions to show all initially
        this.filteredSubscriptions = [...this.subscriptions];
        
        console.log('Loaded subscriptions with customer data:', this.subscriptions);
      }
    } catch (error) {
      console.error('Error loading subscriptions:', error);
    }
  }

  async loadLeads() {
    try {
      // Load leads for sales appointments
      const response = await this.ams.getItemsForModel('Lead');
      
      if (response && response.data) {
        this.leads = response.data;
        console.log('Loaded leads:', this.leads);
      }
    } catch (error) {
      console.error('Error loading leads:', error);
    }
  }

  /**
   * Loads every ServiceType record for the required Service Type dropdown.
   * An empty result drives the template's "no Service Types exist yet -
   * create one" prompt (a link to the generic /dashboard/form/ServiceType/-1
   * create route) rather than leaving the dropdown silently empty.
   */
  async loadServiceTypes() {
    try {
      const response = await this.ams.getItemsForModel('ServiceType');

      if (response && response.data) {
        this.serviceTypes = response.data;
        console.log('Loaded service types:', this.serviceTypes);
      }
    } catch (error) {
      console.error('Error loading service types:', error);
    }
  }

  /**
   * Loads the Services already linked to `selectedServiceAddress` via
   * ServiceServiceAddress, scoping the multi-select Services field's
   * options to exactly that set. Called from handleServiceAddressResult()
   * whenever the ServiceAddress changes; clears servicesForSelectedAddress
   * (and any now-invalid selections) when there's no ServiceAddress at all.
   */
  async loadServicesForSelectedAddress() {
    this.servicesForSelectedAddress = [];
    this.appointmentData.serviceIds = [];

    if (!this.selectedServiceAddress) return;

    const config = {
      relationshipModelName: 'ServiceServiceAddress',
      baseModelName: 'ServiceAddress',
      associatedWith: 'serviceAddressId',
    };
    const related = this.ams.getRelatedItems(config, this.selectedServiceAddress.id);
    if (!related) return;

    const { items } = await firstValueFrom(related.pipe(filter((r: any) => r.isSynced)));
    this.servicesForSelectedAddress = items.map((i: any) => i.service).filter((s: any) => !!s);
  }

  onAppointmentTypeChange(event: any) {
    const appointmentType = event.value;
    this.filterSchedulesByType(appointmentType);
    
    // Clear schedule selection when type changes
    this.appointmentData.scheduleId = '';
    
    // Clear location selection when type changes
    this.selectedLocationRecord = null;
    this.selectedServiceAddress = null;
    this.linkedSubsForServiceAddress = [];
    this.servicesForSelectedAddress = [];
    this.appointmentData.serviceIds = [];

    // Clear customer filter when type changes
    this.selectedCustomerFilter = null;
    this.filteredSubscriptions = [...this.subscriptions];
  }

  /**
   * Fixed appointment_type -> target User role map (Req 15.1 - appointment_type
   * is now a static enum, not FieldConfig/Choices-driven). SERVICE/SALES keep
   * their existing role filters; CONSULTATION has no fixed role filter and
   * falls through to the default (matching the prior fallback targetRole logic
   * for anything that wasn't 'SERVICE').
   */
  private readonly appointmentTypeRoleMap: Record<string, string> = {
    SERVICE: 'TECHNICIAN',
    SALES: 'SALES_REP',
  };

  filterSchedulesByType(appointmentType: string) {
    if (!appointmentType) {
      this.filteredSchedules = [];
      return;
    }

    const targetRole = this.appointmentTypeRoleMap[appointmentType] || 'SALES_REP';

    this.filteredSchedules = this.allSchedules.filter(schedule => {
      if (!schedule.user || !schedule.user.roles) return false;
      return schedule.user.roles.includes(targetRole);
    });

    console.log(`Filtered schedules for ${appointmentType} (${targetRole}):`, this.filteredSchedules);
  }

  /**
   * Get display name for appointment type (for error messages)
   */
  getAppointmentTypeDisplay(appointmentType: string): string {
    const option = this.appointmentTypeOptions.find(o => o.value === appointmentType);
    return option ? option.title : appointmentType;
  }

  /**
   * Determine if the current appointment is a service appointment
   */
  isServiceAppointment(): boolean {
    if (!this.appointmentData.appointment_type) return false;
    
    // Check if appointment type is service-related
    const appointmentType = this.appointmentData.appointment_type.toUpperCase();
    return appointmentType === 'SERVICE' || appointmentType.includes('SERVICE');
  }

  /**
   * Handle location record selection change
   */
  onLocationRecordChange(event: any) {
    this.selectedLocationRecord = event.value;
    console.log('Selected location record:', this.selectedLocationRecord);
  }

  /**
   * Req 1.1/1.2 - opens ServiceAddressPopupComponent (existing/select or
   * create-new) as the ServiceAddress selection step. Used by both SERVICE
   * (ServiceAddress-first location step, replacing Sub selection) and SALES
   * (added alongside the unchanged Lead-selection flow). For SALES, when a
   * Lead is already selected, a synthetic `customer.contact.address.zip_code`
   * is passed so a newly-created ServiceAddress is prepopulated with that
   * Lead's zip code - ServiceAddressPopupComponent itself is unmodified.
   */
  openServiceAddressPopup() {
    const isSales = this.appointmentData.appointment_type === 'SALES';
    const dialogRef = this.dialog.open(ServiceAddressPopupComponent, {
      data: {
        customer: (isSales && this.selectedLocationRecord)
          ? { contact: { address: { zip_code: this.selectedLocationRecord.zipCode } } }
          : null,
        excludeServiceAddressIds: [],
      } as ServiceAddressPopupData,
    });
    dialogRef.afterClosed().subscribe((result) => this.handleServiceAddressResult(result));
  }

  /**
   * Req 1.2/1.6/1.7/1.8 - handles the ServiceAddressPopupComponent dialog
   * result. Dismissing the popup (result is undefined) leaves any prior
   * selection unchanged. Resolving a ServiceAddress (existing or newly
   * created) sets selectedServiceAddress and, for SERVICE appointments,
   * derives the Sub reference from that ServiceAddress's linked Subs.
   */
  async handleServiceAddressResult(result: { serviceAddress: any; isNew: boolean } | undefined) {
    if (!result) return; // Req 1.11-equivalent for the Appointment_Builder: dismissal changes nothing
    this.selectedServiceAddress = result.serviceAddress;
    if (this.isServiceAppointment()) {
      await this.deriveSubFromServiceAddress();
      await this.loadServicesForSelectedAddress();
    }
    this.updateAppointmentName();
  }

  /**
   * Req 1.6/1.7/1.8 - derives the ScheduleEntry's Sub reference from the
   * selected ServiceAddress's linked-Sub cardinality (via the
   * SubServiceAddress join): auto-set when exactly one linked Sub, populate
   * linkedSubsForServiceAddress for an explicit picker when more than one,
   * and leave subId unset when zero.
   */
  async deriveSubFromServiceAddress() {
    if (!this.selectedServiceAddress) return;

    this.linkedSubsForServiceAddress = [];

    const config = {
      relationshipModelName: 'SubServiceAddress',
      baseModelName: 'ServiceAddress',
      associatedWith: 'serviceAddressId',
    };
    const related = this.ams.getRelatedItems(config, this.selectedServiceAddress.id);
    if (!related) {
      this.appointmentData.subId = '';
      return;
    }

    const { items } = await firstValueFrom(
      related.pipe(filter((r: any) => r.isSynced))
    );

    if (items.length === 1) {
      this.appointmentData.subId = items[0].sub.id; // Req 1.6
    } else if (items.length > 1) {
      this.linkedSubsForServiceAddress = items.map((i: any) => i.sub); // Req 1.7 - template renders a picker
      this.appointmentData.subId = '';
    } else {
      this.appointmentData.subId = ''; // Req 1.8
    }
  }

  /**
   * Handle customer filter change (for subscription filtering)
   */
  onCustomerFilterChange(event: any) {
    this.selectedCustomerFilter = event.value;
    this.filterSubscriptionsByCustomer();
    
    // Clear subscription selection when customer filter changes
    this.selectedLocationRecord = null;
  }

  /**
   * Filter subscriptions by selected customer
   */
  filterSubscriptionsByCustomer() {
    if (!this.selectedCustomerFilter) {
      // Show all subscriptions if no customer filter selected
      this.filteredSubscriptions = [...this.subscriptions];
    } else {
      // Filter subscriptions by selected customer
      this.filteredSubscriptions = this.subscriptions.filter(sub => 
        sub.customerId === this.selectedCustomerFilter.id
      );
    }
    
    console.log('Filtered subscriptions by customer:', this.filteredSubscriptions);
  }

  /**
   * Display text for the read-only "Related Subscription" field on SERVICE
   * appointments (Details step). The Sub reference for a SERVICE
   * appointment is always derived from the selected ServiceAddress (see
   * deriveSubFromServiceAddress()), not chosen manually here - this just
   * resolves appointmentData.subId to a display name, checking
   * linkedSubsForServiceAddress first (populated when the ServiceAddress
   * has more than one linked Sub, in which case the technician already
   * made an explicit choice in the Location step) and falling back to the
   * full `subscriptions` list otherwise.
   */
  get relatedSubscriptionDisplay(): string {
    if (!this.appointmentData.subId) return 'None';

    const sub =
      this.linkedSubsForServiceAddress.find((s) => s.id === this.appointmentData.subId) ||
      this.subscriptions.find((s) => s.id === this.appointmentData.subId);

    if (!sub) return 'None';
    return sub.customer?.name ? `${sub.name} - ${sub.customer.name}` : sub.name;
  }

  /**
   * Get subscription address string for display in dropdown
   */
  getSubscriptionAddress(subscription: any): string | null {
    // First try service_address if service is at customer address
    if (subscription.service_at_customer_address && subscription.service_address) {
      return this.formatAddressToString(subscription.service_address);
    }
    
    // Fallback to customer's contact address
    if (subscription.customer && subscription.customer.contact && subscription.customer.contact.address) {
      return this.formatAddressToString(subscription.customer.contact.address);
    }
    
    return null;
  }

  /**
   * Get subscription address object for detailed display
   */
  getSubscriptionAddressObject(subscription: any): any {
    // First try service_address if service is at customer address
    if (subscription.service_at_customer_address && subscription.service_address) {
      return subscription.service_address;
    }
    
    // Fallback to customer's contact address
    if (subscription.customer && subscription.customer.contact && subscription.customer.contact.address) {
      return subscription.customer.contact.address;
    }
    
    return null;
  }

  /**
   * Helper method to format address to string for dropdown preview
   */
  private formatAddressToString(address: any): string {
    if (!address) return '';
    
    let addressParts: string[] = [];
    
    if (address.street_address) addressParts.push(address.street_address);
    if (address.city) addressParts.push(address.city);
    if (address.state) addressParts.push(address.state);
    
    return addressParts.length > 0 ? addressParts.join(', ') : '';
  }

  /**
   * Format address object for display
   */
  formatAddressForDisplay(address: any): string {
    if (!address) return 'No address available';
    
    let addressParts: string[] = [];
    
    if (address.street_address) {
      let streetLine = address.street_address;
      if (address.unit_number) {
        streetLine += ` ${address.unit_number}`;
      }
      addressParts.push(streetLine);
    }
    
    if (address.city || address.state || address.zip_code) {
      let cityStateLine: string[] = [];
      if (address.city) cityStateLine.push(address.city);
      if (address.state) cityStateLine.push(address.state);
      if (address.zip_code) cityStateLine.push(address.zip_code);
      addressParts.push(cityStateLine.join(', '));
    }
    
    return addressParts.join('\n');
  }

  /**
   * Check if schedule step is complete
   */
  isScheduleStepComplete(): boolean {
    return !!(this.appointmentData.appointment_type && this.appointmentData.scheduleId);
  }

  /**
   * Check if details form is valid
   */
  isDetailsFormValid(): boolean {
    return !!(
      this.appointmentData.name &&
      this.appointmentData.start_date &&
      this.appointmentData.start_time &&
      this.appointmentData.end_date &&
      this.appointmentData.end_time &&
      this.appointmentData.serviceTypeId // NEW - required Service Type field
    );
  }

  /**
   * Req 1.2/1.3/1.4 - the location step is complete when:
   * - CONSULTATION: unconditionally (no ServiceAddress required)
   * - SALES: both the Lead (selectedLocationRecord) and the ServiceAddress are selected
   * - SERVICE: the ServiceAddress is selected, and if it's linked to more than one
   *   Sub, an explicit Sub choice (appointmentData.subId) has been made
   */
  isLocationStepComplete(): boolean {
    const appointmentType = this.appointmentData.appointment_type;

    if (appointmentType === 'CONSULTATION') {
      return true;
    }

    if (appointmentType === 'SALES') {
      return !!this.selectedLocationRecord && !!this.selectedServiceAddress;
    }

    if (appointmentType === 'SERVICE') {
      if (!this.selectedServiceAddress) {
        return false;
      }
      if (this.linkedSubsForServiceAddress.length > 1) {
        return !!this.appointmentData.subId;
      }
      return true;
    }

    return false;
  }

  isAppointmentFormValid(): boolean {
    // Update to check all required fields across all steps
    return this.isScheduleStepComplete() && 
           this.isLocationStepComplete() && 
           this.isDetailsFormValid();
  }

  async onAppointmentFormSubmit() {
    if (!this.isAppointmentFormValid()) {
      return;
    }

    // Validate time restrictions
    if (!this.validateTimeRestrictions()) {
      return;
    }

    try {
      // Combine date and time for start and end datetime
      const startDateTime = this.combineDateAndTime(this.appointmentData.start_date, this.appointmentData.start_time);
      const endDateTime = this.combineDateAndTime(this.appointmentData.end_date, this.appointmentData.end_time);

      // For service appointments, link to the selected subscription
      let subId = this.appointmentData.subId;
      if (this.isServiceAppointment() && this.selectedLocationRecord && this.selectedLocationRecord.id) {
        subId = this.selectedLocationRecord.id;
      }

      const appointmentPayload = {
        appointment_type: this.appointmentData.appointment_type,
        scheduleId: this.appointmentData.scheduleId,
        name: this.appointmentData.name,
        start_date_time: startDateTime.toISOString(),
        end_date_time: endDateTime.toISOString(),
        subId: subId || undefined,
        serviceTypeId: this.appointmentData.serviceTypeId,
        // Only meaningful for SERVICE appointments - undefined (rather than
        // an empty array) for anything else, so the handler's
        // serviceIds.length > 0 check behaves the same either way.
        serviceIds: this.isServiceAppointment() && this.appointmentData.serviceIds?.length > 0
          ? this.appointmentData.serviceIds
          : undefined
      };

      console.log('Creating appointment with payload:', appointmentPayload);

      const result = await this.ams.createScheduleEntryValidated({
        ...appointmentPayload,
        serviceAddressId: this.selectedServiceAddress?.id
      });
      
      if (result.data) {
        this.appointmentObj = result.data;
        this.appointmentMessage = "Successfully created appointment!";
        
        // Auto-advance to completion
        setTimeout(() => {
          this.onComplete();
        }, 1000);
      } else if (result.errors) {
        console.error('Appointment creation errors:', result.errors);
        this.appointmentMessage = "Error creating appointment. Please try again.";
      }
    } catch (error) {
      console.error('Error creating appointment:', error);
      this.appointmentMessage = "Error creating appointment. Please try again.";
    }
  }

  /**
   * Handle time change events to enforce restrictions
   */
  onTimeChange(field: string, time: Date) {
    if (!time) return;

    // Apply time restrictions
    const restrictedTime = this.applyTimeRestrictions(time);
    
    // Update the model with the restricted time
    if (field === 'start_time') {
      this.appointmentData.start_time = restrictedTime;
    } else if (field === 'end_time') {
      this.appointmentData.end_time = restrictedTime;
    }

    // Clear any previous error messages
    if (this.appointmentMessage && (this.appointmentMessage.includes('Start time must be') || this.appointmentMessage.includes('End time must be'))) {
      this.appointmentMessage = '';
    }
  }

  /**
   * Validate that selected times are within configured restrictions
   */
  validateTimeRestrictions(): boolean {
    if (!this.timeRestrictions.minTime || !this.timeRestrictions.maxTime) {
      return true; // No restrictions configured
    }

    const startTime = this.appointmentData.start_time;
    const endTime = this.appointmentData.end_time;

    if (!startTime || !endTime) {
      return true; // Times not selected yet
    }

    const startTimeString = startTime.toTimeString().substring(0, 5);
    const endTimeString = endTime.toTimeString().substring(0, 5);

    const startMinutes = this.convertTimeToMinutes(startTimeString);
    const endMinutes = this.convertTimeToMinutes(endTimeString);
    const minMinutes = this.convertTimeToMinutes(this.timeRestrictions.minTime);
    const maxMinutes = this.convertTimeToMinutes(this.timeRestrictions.maxTime);

    if (startMinutes < minMinutes || startMinutes > maxMinutes) {
      this.appointmentMessage = `Start time must be between ${this.timeRestrictions.minTime} and ${this.timeRestrictions.maxTime}`;
      return false;
    }

    if (endMinutes < minMinutes || endMinutes > maxMinutes) {
      this.appointmentMessage = `End time must be between ${this.timeRestrictions.minTime} and ${this.timeRestrictions.maxTime}`;
      return false;
    }

    return true;
  }

  private combineDateAndTime(date: Date, time: Date): Date {
    if (!date || !time) return new Date();
    
    const combined = new Date(date);
    combined.setHours(time.getHours(), time.getMinutes(), 0, 0);
    return combined;
  }

  /**
   * Marks the appointment name as manually edited, so updateAppointmentName()
   * stops overwriting it going forward. Bound to the Appointment Name
   * field's own (ngModelChange) - since setting appointmentData.name
   * *from* updateAppointmentName() itself also fires (ngModelChange), that
   * call temporarily suppresses this flag via `isAutoUpdatingName` so an
   * auto-generated update doesn't immediately mark itself as "manually
   * edited".
   */
  private isAutoUpdatingName = false;
  onNameManuallyEdited() {
    if (this.isAutoUpdatingName) return;
    this.nameManuallyEdited = true;
  }

  /** Service Type dropdown change handler - recomputes End_Date_Time and the appointment name. */
  onServiceTypeChange() {
    this.updateEndDateTime();
    this.updateAppointmentName();
  }

  /** Services multi-select change handler - only the appointment name depends on this. */
  onServicesChange() {
    this.updateAppointmentName();
  }

  /** Start Date/Start Time change handler - recomputes End_Date_Time and the appointment name (month may have changed). */
  onStartDateTimeChange() {
    this.updateEndDateTime();
    this.updateAppointmentName();
  }

  /**
   * Recomputes End_Date/End_Time from Start_Date/Start_Time plus the
   * selected Service Type's default_time_minutes, whenever either changes.
   * No-ops when Start_Date/Start_Time or a Service Type aren't set yet -
   * the technician can still fill in End_Date/End_Time manually in that
   * case (isDetailsFormValid() still requires them either way).
   */
  updateEndDateTime() {
    const serviceType = this.serviceTypes.find((st) => st.id === this.appointmentData.serviceTypeId);
    if (!serviceType || !this.appointmentData.start_date || !this.appointmentData.start_time) return;

    const startDateTime = this.combineDateAndTime(this.appointmentData.start_date, this.appointmentData.start_time);
    const endDateTime = new Date(startDateTime.getTime() + serviceType.default_time_minutes * 60 * 1000);

    this.appointmentData.end_date = new Date(endDateTime);
    this.appointmentData.end_time = this.applyTimeRestrictions(new Date(endDateTime));
  }

  /**
   * Auto-constructs the appointment name as
   * "<Property Owner Name> - <Service(s)> - <Service Type> - <Month>"
   * (using "Multiple Services" in place of the Service(s) segment when more
   * than one Service is selected). Skips overwriting once the technician
   * has manually edited the name field (see nameManuallyEdited/
   * onNameManuallyEdited()). Any segment whose source value isn't
   * available yet is simply omitted (rather than rendering a blank/
   * "undefined" placeholder), so the name still reads sensibly while the
   * form is only partially filled out.
   */
  updateAppointmentName() {
    if (this.nameManuallyEdited) return;

    const propertyOwnerName = this.selectedServiceAddress?.property_owner_name || '';

    let servicesSegment = '';
    if (this.isServiceAppointment() && this.appointmentData.serviceIds?.length > 0) {
      servicesSegment =
        this.appointmentData.serviceIds.length > 1
          ? 'Multiple Services'
          : this.servicesForSelectedAddress.find((s) => s.id === this.appointmentData.serviceIds[0])?.title || '';
    }

    const serviceType = this.serviceTypes.find((st) => st.id === this.appointmentData.serviceTypeId);
    const serviceTypeSegment = serviceType?.name || '';

    const monthSegment = this.appointmentData.start_date
      ? new Date(this.appointmentData.start_date).toLocaleString('en-US', { month: 'long' })
      : '';

    const segments = [propertyOwnerName, servicesSegment, serviceTypeSegment, monthSegment].filter((s) => !!s);

    this.isAutoUpdatingName = true;
    this.appointmentData.name = segments.join(' - ');
    this.isAutoUpdatingName = false;
  }

  onCancel() {
    this.dialogRef.close();
  }

  onComplete() {
    console.log('Appointment creation completed', this.appointmentObj);
    this.dialogRef.close(this.appointmentObj);
  }
}
